import { currentDivisionId } from 'common/src/stores/useFiltersStore';
import React, { useEffect, useState } from 'react';
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Select,
  SelectItem,
  Button,
  Input,
  Card,
} from '@nextui-org/react';
import { getSettlementsByExporterId } from 'common/src/api/settlements';
import { getClaimsByExporterId } from 'common/src/api/claims';
import {
  Settlement,
  SettlementDetails,
  SettlementStatus,
} from 'common/src/utils/types';
import { formatMoneyUSD } from '../../utils/functions';
import useUserStore from '../../../../common/src/stores/useUserStore';
import useDataStore from '../../../../common/src/stores/useDataStore';
import useFilterStore from '../../../../common/src/stores/useFiltersStore';
import SeasonFilter from '../../../../common/src/components/filters/SeasonFilter';
import ModalSlideFilter from '../../../../common/src/components/filters/modal-sidebar-filter';
import Header from '../../components/layouts/Header';
import {
  EmptyState,
  FiltersToggle,
  LoadingState,
  PageLayout,
} from '../../components/ui';

// Columns definition with category
const columns = [
  { name: 'CLIENTE', uid: 'name', category: 'neutral' },
  { name: 'VENTA USD', uid: 'venta', category: 'positive' },
  { name: 'COMISIÓN USD', uid: 'comision', category: 'negative' },
  { name: 'TAX USD', uid: 'tax', category: 'negative' },
  { name: 'FLETE USD', uid: 'flete', category: 'negative' },
  { name: 'OC USD', uid: 'oc', category: 'negative' },
  { name: 'FOB USD', uid: 'fob', category: 'positive' },
  { name: 'COMPENSACIÓN', uid: 'compensacion', category: 'positive' },
  { name: 'COMISIÓN EXP', uid: 'comisionExp', category: 'negative' },
  { name: 'COSTOS EMB', uid: 'costosEmb', category: 'negative' },
  { name: 'RETORNO PROD', uid: 'retornoProd', category: 'result' },
];

// Client data interface
interface ClientData {
  name: string;
  venta: number;
  comision: number;
  tax: number;
  flete: number;
  oc: number;
  fob: number;
  compensacion: number;
  comisionExp: number;
  costosEmb: number;
  retornoProd: number;
}

// Box Format interface
interface BoxFormat {
  format: string;
  unitWeight: number;
  formatKey: string;
  totalKilos: number;
}

// Extend SettlementDetails to include boxes property
interface ExtendedSettlementDetails extends SettlementDetails {
  boxes?: number;
}

// Filter options
const sinisterOptions = [
  { key: 'all', label: 'Todos' },
  { key: 'sinister', label: 'Solo Siniestrados' },
  { key: 'non_sinister', label: 'Solo No Siniestrados' },
];

const incotermOptions = [
  { key: 'all', label: 'Todos' },
  { key: 'FOB', label: 'Solo FOB' },
  { key: 'CIF', label: 'Solo CIF' },
];

const DEFAULT_COMMISSION_RATE = 8; // 8%
const DEFAULT_PACKAGING_COST = 2; // 2 USD per kg

const BusinessPage: React.FC = () => {
  const { userProfile } = useUserStore();
  const { selectedSpecies } = useDataStore();
  const { selectedSeason } = useFilterStore();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [clientData, setClientData] = useState<ClientData[]>([]);
  const [totals, setTotals] = useState<ClientData>({
    name: 'TOTAL',
    venta: 0,
    comision: 0,
    tax: 0,
    flete: 0,
    oc: 0,
    fob: 0,
    compensacion: 0,
    comisionExp: 0,
    costosEmb: 0,
    retornoProd: 0,
  });
  const [hasData, setHasData] = useState<boolean>(true);
  const [boxFormats, setBoxFormats] = useState<BoxFormat[]>([]);

  // Simulation variables
  const [commissionRate, setCommissionRate] = useState<number>(
    DEFAULT_COMMISSION_RATE
  );
  const [packagingCosts, setPackagingCosts] = useState<Record<string, number>>(
    {}
  );
  const [originalSettlements, setOriginalSettlements] = useState<Settlement[]>(
    []
  );
  const [claims, setClaims] = useState<any[]>([]);

  // Filter states
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(true);
  const [selectedSinister, setSelectedSinister] = useState<string>('all');
  const [selectedIncoterm, setSelectedIncoterm] = useState<string>('all');

  useEffect(() => {
    fetchSettlements();
  }, [selectedSpecies, selectedSeason, selectedSinister, selectedIncoterm]);

  useEffect(() => {
    if (originalSettlements.length > 0) {
      recalculateData();
    }
  }, [commissionRate, packagingCosts, claims]);

  const fetchSettlements = async () => {
    if (!userProfile?.exporter_id) {
      return;
    }
    setIsLoading(true);
    try {
      console.log(userProfile?.exporter_id);

      const [settlementsData, claimsData] = await Promise.all([
        getSettlementsByExporterId(
          userProfile?.exporter_id,
          'approved' as SettlementStatus,
          selectedSeason,
          selectedSpecies?.id,
          selectedSinister as 'all' | 'sinister' | 'non_sinister',
          ['*'],
          currentDivisionId()
        ),
        getClaimsByExporterId(userProfile.exporter_id),
      ]);

      setClaims(claimsData || []);

      if (settlementsData && settlementsData.length > 0) {
        // Apply incoterm filter here (not part of the API)
        const filteredSettlements =
          selectedIncoterm === 'all'
            ? settlementsData
            : settlementsData.filter(
              (s) => s.original_incoterm === selectedIncoterm
            );

        if (filteredSettlements.length > 0) {
          setOriginalSettlements(filteredSettlements);
          extractBoxFormats(filteredSettlements);
          initializePackagingCosts(filteredSettlements);
          processSettlements(filteredSettlements, claimsData || []);
          setHasData(true);
        } else {
          // No settlements after filtering
          setClientData([]);
          setTotals({
            name: 'TOTAL',
            venta: 0,
            comision: 0,
            tax: 0,
            flete: 0,
            oc: 0,
            fob: 0,
            compensacion: 0,
            comisionExp: 0,
            costosEmb: 0,
            retornoProd: 0,
          });
          setHasData(false);
          setIsLoading(false);
        }
      } else {
        // No settlements returned from API
        setClientData([]);
        setTotals({
          name: 'TOTAL',
          venta: 0,
          comision: 0,
          tax: 0,
          flete: 0,
          oc: 0,
          fob: 0,
          compensacion: 0,
          comisionExp: 0,
          costosEmb: 0,
          retornoProd: 0,
        });
        setHasData(false);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setHasData(false);
      setIsLoading(false);
    }
  };

  const extractBoxFormats = (settlements: Settlement[]) => {
    const formats: Record<string, BoxFormat> = {};

    settlements.forEach((settlement) => {
      settlement.settlement_details.forEach(
        (detail: ExtendedSettlementDetails) => {
          if (detail.format && detail.unit_weight) {
            const formatKey = `${detail.format}-${detail.unit_weight}`;

            if (!formats[formatKey]) {
              formats[formatKey] = {
                format: detail.format,
                unitWeight: detail.unit_weight,
                formatKey,
                totalKilos: 0,
              };
            }

            // Use the existing kilos field
            formats[formatKey].totalKilos += detail.kilos || 0;
          }
        }
      );
    });

    setBoxFormats(Object.values(formats));
  };

  const initializePackagingCosts = (settlements: Settlement[]) => {
    const newPackagingCosts: Record<string, number> = {};

    // Extract unique formats
    const uniqueFormats = new Set<string>();
    settlements.forEach((settlement) => {
      settlement.settlement_details.forEach((detail: SettlementDetails) => {
        if (detail.format && detail.unit_weight) {
          const formatKey = `${detail.format}-${detail.unit_weight}`;
          uniqueFormats.add(formatKey);
        }
      });
    });

    // Initialize all with default cost
    uniqueFormats.forEach((format) => {
      newPackagingCosts[format] = DEFAULT_PACKAGING_COST;
    });

    setPackagingCosts(newPackagingCosts);
  };

  const recalculateData = () => {
    if (originalSettlements.length === 0) return;
    processSettlements(originalSettlements, claims);
  };

  const calculatePackagingCost = (detail: SettlementDetails): number => {
    if (!detail.format || !detail.unit_weight) return 0;

    const formatKey = `${detail.format}-${detail.unit_weight}`;
    const costPerKg = packagingCosts[formatKey] || DEFAULT_PACKAGING_COST;

    // Use the kilos field directly
    return (detail.kilos || 0) * costPerKg;
  };

  const processSettlements = (
    settlements: Settlement[],
    currentClaims: any[]
  ) => {
    // Create client groups
    const clientGroups: Record<string, Settlement[]> = {};

    // Group settlements by client (receiver)
    settlements.forEach((settlement) => {
      const clientName = settlement.receiver;
      if (!clientGroups[clientName]) {
        clientGroups[clientName] = [];
      }
      clientGroups[clientName].push(settlement);
    });

    // Calculate metrics for each client
    const processedClients: ClientData[] = Object.entries(clientGroups).map(
      ([clientName, clientSettlements]) => {
        // Initialize client data
        const client: ClientData = {
          name: clientName,
          venta: 0,
          comision: 0,
          tax: 0,
          flete: 0,
          oc: 0,
          fob: 0,
          compensacion: 0,
          comisionExp: 0,
          costosEmb: 0,
          retornoProd: 0,
        };

        // Process each settlement
        clientSettlements.forEach((settlement) => {
          // Process each settlement detail
          settlement.settlement_details.forEach(
            (detail: ExtendedSettlementDetails) => {
              if (detail.exchange_rate) {
                // venta: local_total_price converted to USD
                client.venta += detail.local_total_price / detail.exchange_rate;

                // comision: local_total_commision converted to USD
                client.comision +=
                  detail.local_total_commision / detail.exchange_rate;

                // tax: local_total_tax converted to USD
                client.tax += detail.local_total_tax / detail.exchange_rate;

                // Add to FOB USD
                client.fob += detail.usd_total_fob || 0;

                // Other costs converted to USD
                client.oc +=
                  ((detail.local_other_costs || 0) +
                    (detail.local_repacking_costs || 0)) /
                  detail.exchange_rate;

                // Calculate packaging cost based on format and weight
                const packagingCost = calculatePackagingCost(detail);
                client.costosEmb += packagingCost;
              }
            }
          );

          // Add freight costs
          client.flete += settlement.total_freight_usd || 0;
        });

        // Add compensations
        currentClaims.forEach((claim) => {
          if (claim.receiver === clientName && claim.status === 'accepted') {
            client.compensacion += claim.total_accepted_usd || 0;
          }
        });

        // Calculate derived values based on the simulation values
        client.comisionExp =
          ((client.fob + client.compensacion) * commissionRate) / 100;
        client.retornoProd =
          client.fob -
          client.costosEmb -
          client.comisionExp +
          client.compensacion;

        return client;
      }
    );

    // Calculate totals
    const calculatedTotals: ClientData = {
      name: 'TOTAL',
      venta: 0,
      comision: 0,
      tax: 0,
      flete: 0,
      oc: 0,
      fob: 0,
      compensacion: 0,
      comisionExp: 0,
      costosEmb: 0,
      retornoProd: 0,
    };

    processedClients.forEach((client) => {
      calculatedTotals.venta += client.venta;
      calculatedTotals.comision += client.comision;
      calculatedTotals.tax += client.tax;
      calculatedTotals.flete += client.flete;
      calculatedTotals.oc += client.oc;
      calculatedTotals.fob += client.fob;
      calculatedTotals.compensacion += client.compensacion;
      calculatedTotals.comisionExp += client.comisionExp;
      calculatedTotals.costosEmb += client.costosEmb;
      calculatedTotals.retornoProd += client.retornoProd;
    });

    // Sort clients by venta (highest first)
    processedClients.sort((a, b) => b.venta - a.venta);

    setClientData(processedClients);
    setTotals(calculatedTotals);
    setIsLoading(false);
  };

  const updatePackagingCost = (formatKey: string, cost: number) => {
    const updatedCosts = { ...packagingCosts };
    updatedCosts[formatKey] = cost;
    setPackagingCosts(updatedCosts);
  };

  const renderCell = (item: ClientData, columnKey: React.Key) => {
    const key = String(columnKey);
    const value = item[key as keyof ClientData];

    if (key === 'name') {
      return (
        <div className={item.name === 'TOTAL' ? 'font-bold' : ''}>{value}</div>
      );
    }

    if (typeof value === 'number') {
      // Find column definition
      const column = columns.find((col) => col.uid === key);

      // Display minus sign for costs (negative category)
      if (column?.category === 'negative' && value > 0) {
        return `-${formatMoneyUSD(value)}`;
      }
      return value > 0 ? formatMoneyUSD(value) : '-';
    }

    return '-';
  };

  if (isLoading) {
    return (
      <LoadingState label='Cargando datos...' />
    );
  }

  // Prepare all data for display (clients + total)
  const allData = [...clientData, totals];

  // Render empty state when there's no data
  const renderEmptyState = () => (
    <EmptyState
      title='No hay datos para los filtros seleccionados'
      action={
        <Button
          variant='flat'
          size='sm'
          onClick={() => {
            setSelectedSinister('all');
            setSelectedIncoterm('all');
          }}
        >
          Reiniciar filtros
        </Button>
      }
    />
  );

  const renderVariablesSection = () => (
    <Card className='p-4 mb-6'>
      <h2 className='text-lg font-semibold mb-4'>Variables</h2>

      <div className='mb-6'>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mb-4'>
          {boxFormats.map((format) => (
            <div key={format.formatKey} className='mb-2'>
              <label className='text-sm mb-1 block'>
                Costo USD/kg{' '}
                <span className='font-mono bg-default-100 px-1 rounded'>
                  {format.formatKey}
                </span>
              </label>
              <Input
                type='number'
                value={
                  packagingCosts[format.formatKey]?.toString() ||
                  DEFAULT_PACKAGING_COST.toString()
                }
                onChange={(e) =>
                  updatePackagingCost(
                    format.formatKey,
                    parseFloat(e.target.value) || DEFAULT_PACKAGING_COST
                  )
                }
                className='max-w-xs'
                size='sm'
                min='0'
                step='0.1'
              />
              <div className='text-xs text-default-400 mt-1'>
                Total: {format.totalKilos.toFixed(2)} kg
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className='mb-4'>
        <label className='block mb-1'>Porcentaje de Comisión Exportadora</label>
        <div className='flex items-center max-w-xs'>
          <Input
            type='number'
            value={commissionRate.toString()}
            onChange={(e) =>
              setCommissionRate(
                parseFloat(e.target.value) || DEFAULT_COMMISSION_RATE
              )
            }
            className='w-full'
            size='sm'
            min='0'
            step='0.1'
          />
          <span className='ml-2'>%</span>
        </div>
        <p className='text-default-400 text-sm mt-1'>
          Puedes simular cuánto cambiaría el valor si modificaras el porcentaje
          de comisión
        </p>
      </div>
    </Card>
  );

  return (
    <div className='flex w-full h-screen overflow-x-hidden'>
      {/* Sticky Sidebar */}
      <ModalSlideFilter
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
      >
        <div className='bg-background p-4 pt-8 h-full overflow-y-auto'>
          <div className='space-y-4'>
            <p className='text-sm text-default-500 mb-1'>Filtros</p>

            {/* Season Filter */}
            <div className='mb-4'>
              <SeasonFilter size='sm' label='Temporada' />
            </div>

            {/* Sinister Filter */}
            <div className='mb-4'>
              <Select
                label='Estado de Siniestro'
                selectedKeys={[selectedSinister]}
                onChange={(e) => setSelectedSinister(e.target.value)}
                size='sm'
              >
                {sinisterOptions.map((option) => (
                  <SelectItem key={option.key} value={option.key}>
                    {option.label}
                  </SelectItem>
                ))}
              </Select>
            </div>

            {/* Incoterm Filter */}
            <div className='mb-4'>
              <Select
                label='Incoterm'
                selectedKeys={[selectedIncoterm]}
                onChange={(e) => setSelectedIncoterm(e.target.value)}
                size='sm'
              >
                {incotermOptions.map((option) => (
                  <SelectItem key={option.key} value={option.key}>
                    {option.label}
                  </SelectItem>
                ))}
              </Select>
            </div>
          </div>
        </div>
      </ModalSlideFilter>

      <PageLayout className='overflow-y-auto'>
        <Header
          title='Resumen Resultados USD'
          description='Análisis de resultados financieros por cliente'
        />

        <div className='mb-4'>
          <FiltersToggle
            isOpen={isFilterOpen}
            onToggle={() => setIsFilterOpen(!isFilterOpen)}
            label={isFilterOpen ? 'Ocultar filtros' : 'Mostrar filtros'}
          />
        </div>

        {hasData && renderVariablesSection()}

        <div className='overflow-x-auto'>
          {hasData ? (
            <Table
              removeWrapper
              aria-label='Tabla de Resumen de Resultados'
              className='mb-8 min-w-full'
            >
              <TableHeader columns={columns}>
                {(column) => (
                  <TableColumn key={column.uid}>{column.name}</TableColumn>
                )}
              </TableHeader>
              <TableBody>
                {allData.map((item) => (
                  <TableRow
                    key={item.name}
                    className={
                      item.name === 'TOTAL'
                        ? 'bg-default-100 font-semibold'
                        : ''
                    }
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={`${item.name}-${column.uid}`}
                        className={`${column.uid === 'name' ? 'text-left' : 'text-right'
                          }`}
                      >
                        {renderCell(item, column.uid)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            renderEmptyState()
          )}
        </div>
      </PageLayout>
    </div>
  );
};

export default BusinessPage;
